import React from 'react';
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
  Chip,
  Button,
  Link,
} from '@mui/material';
import {
  Code as CodeIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  School as SchoolIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { brand, brandMessaging } from '@penticton-dev-events/shared';
import SEOHelmet from '../components/seo/SEOHelmet';
import { PageContainer, Hero, Section } from '../components/layout';
import { generateOrganizationSchema } from '../lib/seo/structured-data';

const AboutPage = () => {
  const organizationSchema = generateOrganizationSchema();

  return (
    <PageContainer>
      <SEOHelmet
        title="About Penticton.dev Events - Our Developer Community"
        description={`${brandMessaging.seo.description}. ${brandMessaging.community.mission}`}
        keywords={brandMessaging.seo.keywords.join(', ')}
        structuredData={organizationSchema}
      />

      {/* Hero Section */}
      <Hero
        title="Penticton's Developer Community"
        subtitle="Connecting local tech enthusiasts through meetups, workshops, and collaboration"
        backgroundVariant="gradient"
      />

      {/* Mission Section */}
      <Section variant="default">
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={3}>
                <Typography variant="h4" component="h2">
                  Why We Exist
                </Typography>
                <Typography variant="body1" paragraph>
                  Penticton.dev Events exists to bring together the talented developers,
                  designers, and tech enthusiasts scattered across the{' '}
                  {brand.location.region}. We believe that when local talent connects,
                  incredible things happen - innovative projects, lasting friendships, and
                  career-changing opportunities.
                </Typography>
                <Typography variant="body1" paragraph>
                  Our mission is simple: create a welcoming space where developers of all
                  skill levels can learn, share knowledge, and grow together. Whether
                  you're just starting your coding journey or you're a seasoned
                  professional, you have a place in our community.
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: 'relative',
                  height: 300,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                }}
              >
                <Stack alignItems="center" spacing={2}>
                  <CodeIcon sx={{ fontSize: 80 }} />
                  <Typography variant="h5" align="center">
                    Code • Connect • Create
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Values Section */}
      <Section variant="compact" background="paper">
        <Container maxWidth="lg">
          <Stack spacing={6}>
            <Box textAlign="center">
              <Typography variant="h4" component="h2" gutterBottom>
                Our Core Values
              </Typography>
              <Typography color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                These principles guide everything we do as a community
              </Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <PeopleIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="h6" gutterBottom>
                      Inclusive Community
                    </Typography>
                    <Typography color="text.secondary">
                      We celebrate diversity and welcome developers of all backgrounds,
                      experience levels, and technologies. Everyone has something valuable
                      to contribute.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Avatar
                      sx={{
                        bgcolor: 'secondary.main',
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <SchoolIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="h6" gutterBottom>
                      Continuous Learning
                    </Typography>
                    <Typography color="text.secondary">
                      Technology evolves rapidly, and so do we. We're committed to sharing
                      knowledge, exploring new technologies, and growing together as
                      professionals.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ height: '100%', textAlign: 'center' }}>
                  <CardContent sx={{ p: 4 }}>
                    <Avatar
                      sx={{
                        bgcolor: 'success.main',
                        width: 64,
                        height: 64,
                        mx: 'auto',
                        mb: 3,
                      }}
                    >
                      <LocationIcon sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Typography variant="h6" gutterBottom>
                      Local Impact
                    </Typography>
                    <Typography color="text.secondary">
                      We're passionate about building a strong tech ecosystem in Penticton
                      and the Okanagan Valley, supporting local businesses and fostering
                      innovation.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Stack>
        </Container>
      </Section>

      {/* Story Section */}
      <Section variant="default">
        <Container maxWidth="md">
          <Stack spacing={4}>
            <Box textAlign="center">
              <Typography variant="h4" component="h2" gutterBottom>
                Our Story
              </Typography>
              <Typography color="text.secondary">
                How Penticton.dev Events came to life
              </Typography>
            </Box>

            <Typography variant="body1" paragraph>
              Founded in February 2025, Penticton.dev Events started with a simple observation:
              Penticton has a growing community of talented developers, but they were
              scattered across different companies, freelance work, and remote positions
              without a central place to connect and collaborate.
            </Typography>

            <Typography variant="body1" paragraph>
              What began as informal coffee chats between local developers has grown into
              a vibrant community that regularly hosts technical talks, workshops,
              hackathons, and networking events. We've seen incredible projects emerge
              from these connections, new friendships form, and careers advance through
              the power of community.
            </Typography>

            <Typography variant="body1" paragraph>
              Today, we're proud to bring together developers working with diverse
              technologies – from web development and mobile apps to data science, DevOps,
              and emerging technologies. Our platform, built by the community for the
              community, reflects our commitment to quality, accessibility, and user
              experience.
            </Typography>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Technologies We Love
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {[
                  'React',
                  'TypeScript',
                  'Node.js',
                  'Python',
                  'Firebase',
                  'AWS',
                  'Docker',
                  'Kubernetes',
                  'GraphQL',
                  'Next.js',
                  'Vue.js',
                  'Go',
                  'Rust',
                  'Machine Learning',
                  'DevOps',
                ].map(tech => (
                  <Chip key={tech} label={tech} variant="outlined" />
                ))}
              </Stack>
            </Box>
          </Stack>
        </Container>
      </Section>

      {/* Get Involved Section */}
      <Section variant="compact" background="paper">
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Card elevation={2} sx={{ p: 4 }}>
                <Stack spacing={3}>
                  <Typography variant="h5" component="h3" fontWeight="bold">
                    Community Impact
                  </Typography>
                  <Box>
                    <Typography variant="body1" paragraph>
                      Together, we're building something special in the{' '}
                      {brand.location.region}. Here's what our community has achieved:
                    </Typography>
                    <Stack spacing={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h4">🚀</Typography>
                        <Box>
                          <Typography variant="h6">Monthly Meetups</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Regular tech talks, workshops, and knowledge sharing sessions
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h4">👥</Typography>
                        <Box>
                          <Typography variant="h6">Growing Community</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Welcoming developers, designers, and tech enthusiasts of all levels
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h4">📚</Typography>
                        <Box>
                          <Typography variant="h6">Knowledge Sharing</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Members teaching members - from beginners to experts
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h4">🤝</Typography>
                        <Box>
                          <Typography variant="h6">Networking Events</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Building connections that lead to collaborations and
                            opportunities
                          </Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={3}>
                <Typography variant="h5" component="h3">
                  How to Get Involved
                </Typography>
                <Typography variant="body1" paragraph>
                  There are many ways to be part of our thriving developer community.
                  Every contribution helps us grow stronger together.
                </Typography>

                <Box>
                  <Typography variant="h6" gutterBottom>
                    Join the Community
                  </Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Attend Meetups
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Join us for technical talks, workshops, and networking events
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Share Your Knowledge
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Give a talk, lead a workshop, or mentor other developers
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Volunteer to Help
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Assist with event organization, venue setup, or community outreach
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Suggest Topics
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Let us know what you'd like to learn or discuss at future events
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <EmailIcon color="action" />
                      <Link href={`mailto:${brand.contact.email}`} color="primary">
                        {brand.contact.email}
                      </Link>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon color="action" />
                      <Typography variant="body2">
                        {brand.location.city}, {brand.location.province},{' '}
                        {brand.location.country}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Section>
    </PageContainer>
  );
};

export default AboutPage;
